import type React from 'react';
import { type CSSProperties, type FC, type RefObject, useRef } from 'react';

import classNames from 'classnames';

import getRandomHex from '@iconik-io/core/src/get_random_hex';
import {
  type AriaProps,
  ariaToReactProps,
} from '@iconik-io/shared-lib/src/react/a11y/aria';

import TooltipTrigger from '../../components/tooltip_trigger/tooltip_trigger';
import { ui } from '../../utils/helpers';
import Button from '../button/button';
import ClearButton from '../forms/components/clear_button/clear_button';

import styles from './input_field.module.css';

export type ValueType = string | undefined;

type Props = {
  name?: string;
  id?: string;
  label?: string;
  value?: ValueType;
  disabled?: boolean;
  readOnly?: boolean;
  canClear?: boolean;
  autoFocus?: boolean;
  autoComplete?: string;
  onBlur?: (
    value: ValueType,
    event:
      | React.FocusEvent<HTMLInputElement>
      | React.FocusEvent<HTMLSelectElement>,
  ) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (
    value: ValueType,
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLButtonElement>,
  ) => void;
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void;
  onValueChange?: (value: ValueType) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  className?: string;
  inputClassName?: string;
  error?: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
  placeholder?: string;
  type?: string;
  inputMode?:
    | 'none'
    | 'text'
    | 'tel'
    | 'url'
    | 'email'
    | 'numeric'
    | 'decimal'
    | 'search';
  style?: CSSProperties;
  aria?: AriaProps;
  hideInputError?: boolean;
  canGeneratePassword?: boolean;
  onGeneratePassword?: () => void;
};

function getAutoCompleteProps(autoComplete: string | undefined) {
  if (autoComplete === undefined) {
    return {};
  }
  if (autoComplete === 'off') {
    return {
      autoComplete: 'off',
      'data-1p-ignore': true,
      'data-lpignore': true,
    };
  }
  return { autoComplete };
}

const InputField: FC<Props> = (props) => {
  const {
    value = '',
    canClear,
    canGeneratePassword,
    onGeneratePassword,
  } = props;
  const randomName = useRef(`${props.name}_${getRandomHex(8)}`);
  return (
    <div className={classNames(styles.root, props.className)}>
      <div className={styles.actions}>
        {Boolean(value && canClear) && (
          <ClearButton
            onClick={(event) => props.onChange?.(undefined, event)}
            aria={{ controls: props.id, label: 'Clear' }}
          />
        )}
        {canGeneratePassword && onGeneratePassword && (
          <TooltipTrigger
            tooltipText="Generate password"
            componentProps={{
              className: styles.generate_button_tooltip,
            }}
            placement="top"
          >
            <Button
              className={styles.generate_button}
              onClick={onGeneratePassword}
              noBorder
              iconName="refresh"
              wideBasis={false}
              aria={{ label: 'Generate password' }}
            />
          </TooltipTrigger>
        )}
      </div>
      <input
        id={props.id}
        {...ui({ 'input-name': props.name })}
        {...getAutoCompleteProps(props.autoComplete)}
        name={randomName.current}
        disabled={props.disabled}
        readOnly={props.readOnly}
        autoFocus={props.autoFocus}
        placeholder={props.placeholder}
        type={props.type}
        inputMode={props.inputMode}
        className={classNames(styles.input, props.inputClassName, {
          [styles.input_clearable]: canClear,
          [styles.input_error]: props.error && !props.hideInputError,
        })}
        style={props.style}
        ref={props.inputRef}
        value={value}
        onBlur={(event) => props.onBlur?.(event.target.value, event)}
        onFocus={props.onFocus}
        onKeyDown={props.onKeyDown}
        onChange={(event) => {
          props.onValueChange?.(event.target.value);
          return props.onChange?.(event.target.value, event);
        }}
        onClick={props.onClick}
        lang={window.navigator.language}
        {...ariaToReactProps(props.aria)}
      />
    </div>
  );
};

export default InputField;
